import { axiosPrivate } from "@/lib/axios/axios-private";
import {
  apiErrorFromEnvelope,
  ApiError,
  extractEnvelopeMessage,
  sanitizeUserMessage,
  toApiError,
  type ApiEnvelope,
} from "@/lib/api/errors";
import { isAxiosError } from "axios";
import type {
  CategoryFormValues,
  Equipment,
  EquipmentFormValues,
  HierarchyListParams,
  HierarchyListResult,
  Intervention,
  InterventionCategory,
  InterventionFormValues,
  Master,
  MasterFormValues,
} from "@/types/hierarchy-types";
import type { BackendApiResponse } from "@/types/audit-log-types";

const ERROR_CONTEXT = "hierarchy" as const;

export type HierarchyMutationResult = {
  message?: string;
};

export function getHierarchyErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message;
  }

  if (isAxiosError(err)) {
    const raw = extractEnvelopeMessage(err.response?.data);
    if (raw) {
      return sanitizeUserMessage(
        raw,
        ERROR_CONTEXT,
        err.response?.status ?? 500,
      );
    }
  }

  return toApiError(err, ERROR_CONTEXT).message;
}

function extractMutationMessage(raw: unknown): string | undefined {
  const envelopeMessage = extractEnvelopeMessage(raw);
  if (envelopeMessage) return envelopeMessage;

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }

  const record = raw as Record<string, unknown>;
  const data = record.data;

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (data && typeof data === "object" && !Array.isArray(data)) {
    return extractEnvelopeMessage(data);
  }

  return undefined;
}

function normalizeStatus(value: unknown): string {
  if (value == null) return "DRAFT";
  return String(value).trim().toUpperCase();
}

function normalizeReportId(raw: Record<string, unknown>): string | null {
  const value = raw.reportId ?? raw.report_id;
  if (value == null || value === "") return null;
  return String(value);
}

function normalizeMaster(raw: Record<string, unknown>): Master | null {
  const id = raw.id;
  const name = raw.name;
  if (id == null || name == null) return null;

  return {
    id: String(id),
    reportId: normalizeReportId(raw),
    name: String(name),
    description:
      raw.description != null ? String(raw.description) : null,
    status: normalizeStatus(raw.status),
    isDefault: Boolean(
      raw.isDefault ?? raw.is_default ?? raw.default ?? raw.isDefaultMaster,
    ),
    hasIncompleteVideos: Boolean(
      raw.hasIncompleteVideos ?? raw.has_incomplete_videos,
    ),
    createdAt: (raw.createdAt ?? raw.created_at) as number | string | null | undefined,
  };
}

function normalizeEquipment(raw: Record<string, unknown>): Equipment | null {
  const id = raw.id;
  const name = raw.name;
  if (id == null || name == null) return null;

  return {
    id: String(id),
    masterId: String(raw.masterId ?? raw.master_id ?? ""),
    reportId: normalizeReportId(raw),
    name: String(name),
    description:
      raw.description != null ? String(raw.description) : null,
    status: normalizeStatus(raw.status),
    createdAt: (raw.createdAt ?? raw.created_at) as number | string | null | undefined,
  };
}

function normalizeCategoryType(
  value: unknown,
): "STATIC" | "DYNAMIC" | null {
  if (value == null || value === "") return null;
  const normalized = String(value).trim().toUpperCase();
  if (normalized === "STATIC" || normalized === "DYNAMIC") return normalized;
  return null;
}

function normalizeCategory(
  raw: Record<string, unknown>,
): InterventionCategory | null {
  const id = raw.id;
  const name = raw.name;
  if (id == null || name == null) return null;

  return {
    id: String(id),
    equipmentId: String(raw.equipmentId ?? raw.equipment_id ?? ""),
    reportId: normalizeReportId(raw),
    name: String(name),
    description:
      raw.description != null ? String(raw.description) : null,
    categoryType: normalizeCategoryType(
      raw.categoryType ?? raw.category_type,
    ),
    status: normalizeStatus(raw.status),
    createdAt: (raw.createdAt ?? raw.created_at) as number | string | null | undefined,
  };
}

function normalizeIntervention(
  raw: Record<string, unknown>,
): Intervention | null {
  const id = raw.id;
  const name = raw.name;
  if (id == null || name == null) return null;

  return {
    id: String(id),
    categoryId: String(
      raw.categoryId ?? raw.interventionCategoryId ?? raw.intervention_category_id ?? "",
    ),
    reportId: normalizeReportId(raw),
    name: String(name),
    description:
      raw.description != null ? String(raw.description) : null,
    status: normalizeStatus(raw.status),
    hasVideo: Boolean(raw.hasVideo ?? raw.has_video),
  };
}

function extractRawItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;

  if (!payload || typeof payload !== "object") return [];

  const record = payload as Record<string, unknown>;
  const keys = ["items", "data", "content", "records"];

  for (const key of keys) {
    const candidate = record[key];
    if (Array.isArray(candidate)) return candidate;
  }

  for (const key of keys) {
    const candidate = record[key];
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      const nested = extractRawItems(candidate);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function parseListResult<T>(
  payload: unknown,
  normalizer: (raw: Record<string, unknown>) => T | null,
  fallbackPage: number,
  fallbackPageSize: number,
): HierarchyListResult<T> {
  const items = extractRawItems(payload)
    .map((entry) =>
      entry && typeof entry === "object" && !Array.isArray(entry)
        ? normalizer(entry as Record<string, unknown>)
        : null,
    )
    .filter((item): item is T => item != null);

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      items,
      page: fallbackPage,
      pageSize: fallbackPageSize,
      total: items.length,
    };
  }

  const record = payload as Record<string, unknown>;
  const pageSource =
    record.data && typeof record.data === "object" && !Array.isArray(record.data)
      ? (record.data as Record<string, unknown>)
      : record;

  return {
    items,
    page:
      typeof pageSource.page === "number"
        ? pageSource.page
        : fallbackPage,
    pageSize:
      typeof pageSource.pageSize === "number"
        ? pageSource.pageSize
        : typeof pageSource.limit === "number"
          ? pageSource.limit
          : fallbackPageSize,
    total:
      typeof pageSource.total === "number"
        ? pageSource.total
        : items.length,
  };
}

function listQueryParams(params: HierarchyListParams = {}) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  return {
    page,
    pageSize,
    ...(params.search?.trim() ? { search: params.search.trim() } : {}),
    ...(params.status?.trim() ? { status: params.status.trim() } : {}),
    ...(params.categoryType?.trim()
      ? { categoryType: params.categoryType.trim() }
      : {}),
    ...(params.sortBy?.trim() ? { sortBy: params.sortBy.trim() } : {}),
    ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    ...(params.dateFrom?.trim() ? { dateFrom: params.dateFrom.trim() } : {}),
    ...(params.dateTo?.trim() ? { dateTo: params.dateTo.trim() } : {}),
  };
}

function getTotalPages(result: HierarchyListResult<unknown>): number {
  if (result.total > 0 && result.pageSize > 0) {
    return Math.max(1, Math.ceil(result.total / result.pageSize));
  }
  return Math.max(1, result.page);
}

async function unwrapEnvelope<T>(
  promise: Promise<{ data: unknown; status: number }>,
): Promise<T> {
  const { data: raw, status } = await promise;
  const httpOk = status >= 200 && status < 300;

  if (raw == null || raw === "" || typeof raw !== "object" || Array.isArray(raw)) {
    if (httpOk) {
      return null as T;
    }
    throw apiErrorFromEnvelope(
      { success: false, code: status, errorMessage: "Request failed." },
      ERROR_CONTEXT,
    );
  }

  const envelope = raw as BackendApiResponse<T>;
  const code = envelope.code ?? status;
  const success =
    envelope.success !== false && (envelope.success === true || httpOk);

  if (!success || code < 200 || code >= 300) {
    throw apiErrorFromEnvelope(envelope, ERROR_CONTEXT);
  }

  return envelope.data as T;
}

async function unwrapMutation(
  promise: Promise<{ data: unknown; status: number }>,
): Promise<HierarchyMutationResult> {
  const { data: raw, status } = await promise;
  const httpOk = status >= 200 && status < 300;

  if (raw == null || raw === "" || typeof raw !== "object" || Array.isArray(raw)) {
    if (httpOk) {
      return {};
    }
    throw apiErrorFromEnvelope(
      { success: false, code: status, errorMessage: "Request failed." },
      ERROR_CONTEXT,
    );
  }

  const envelope = raw as BackendApiResponse<unknown>;
  const code = envelope.code ?? status;
  const success =
    envelope.success !== false && (envelope.success === true || httpOk);

  if (!success || code < 200 || code >= 300) {
    const apiMessage = extractMutationMessage(raw);
    if (apiMessage) {
      throw new ApiError(sanitizeUserMessage(apiMessage, ERROR_CONTEXT, code), code);
    }
    throw apiErrorFromEnvelope(envelope as ApiEnvelope, ERROR_CONTEXT);
  }

  return { message: extractMutationMessage(raw) };
}

function unwrapEntityPayload(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }

  const record = data as Record<string, unknown>;
  if (record.id != null) {
    return record;
  }

  const nested = record.record ?? record.item ?? record.entity;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }

  return record;
}

function toMasterBody(values: MasterFormValues) {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    ...(values.status ? { status: values.status } : {}),
  };
}

function toEquipmentBody(values: EquipmentFormValues) {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    status: values.status,
  };
}

function toCategoryBody(values: CategoryFormValues) {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    categoryType: values.categoryType ?? null,
    status: values.status,
  };
}

function toInterventionBody(values: InterventionFormValues) {
  return {
    name: values.name.trim(),
    description: values.description.trim() || null,
    status: values.status,
  };
}

export async function fetchMasters(
  params: HierarchyListParams = {},
): Promise<HierarchyListResult<Master>> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>("/masters", {
      params: listQueryParams(params),
    }),
  );

  return parseListResult(data, normalizeMaster, page, pageSize);
}

export async function fetchMaster(id: string): Promise<Master> {
  const trimmedId = id.trim();
  if (!trimmedId) {
    throw apiErrorFromEnvelope(
      { success: false, code: 400, errorMessage: "Master ID is required." },
      ERROR_CONTEXT,
    );
  }

  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(trimmedId)}`,
    ),
  );

  const payload = unwrapEntityPayload(data);
  if (payload) {
    const master = normalizeMaster(payload);
    if (master) return master;
  }

  throw apiErrorFromEnvelope(
    { success: false, code: 404, errorMessage: "Master not found." },
    ERROR_CONTEXT,
  );
}

export async function createMaster(values: MasterFormValues): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.post<BackendApiResponse<unknown>>("/masters", toMasterBody(values)),
  );
}

export async function updateMaster(
  id: string,
  values: MasterFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.patch<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(id.trim())}`,
      toMasterBody(values),
    ),
  );
}

export async function markMasterActive(
  id: string,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.patch<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(id.trim())}`,
      { status: "ACTIVE" },
    ),
  );
}

export async function markMasterDraft(
  id: string,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.patch<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(id.trim())}`,
      { status: "DRAFT" },
    ),
  );
}

export async function deleteMaster(id: string): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.delete<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(id.trim())}`,
    ),
  );
}

export async function fetchEquipmentByMaster(
  masterId: string,
  params: HierarchyListParams = {},
): Promise<HierarchyListResult<Equipment>> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const trimmedId = masterId.trim();

  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(trimmedId)}/equipment`,
      { params: listQueryParams(params) },
    ),
  );

  return parseListResult(data, normalizeEquipment, page, pageSize);
}

export async function fetchEquipment(id: string): Promise<Equipment> {
  const trimmedId = id.trim();
  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/equipment/${encodeURIComponent(trimmedId)}`,
    ),
  );

  const payload = unwrapEntityPayload(data);
  if (payload) {
    const equipment = normalizeEquipment(payload);
    if (equipment) return equipment;
  }

  throw apiErrorFromEnvelope(
    { success: false, code: 404, errorMessage: "Equipment not found." },
    ERROR_CONTEXT,
  );
}

export async function createEquipment(
  masterId: string,
  values: EquipmentFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.post<BackendApiResponse<unknown>>(
      `/masters/${encodeURIComponent(masterId.trim())}/equipment`,
      toEquipmentBody(values),
    ),
  );
}

export async function updateEquipment(
  id: string,
  values: EquipmentFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.patch<BackendApiResponse<unknown>>(
      `/equipment/${encodeURIComponent(id.trim())}`,
      toEquipmentBody(values),
    ),
  );
}

export async function deleteEquipment(id: string): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.delete<BackendApiResponse<unknown>>(
      `/equipment/${encodeURIComponent(id.trim())}`,
    ),
  );
}

export async function fetchInterventionCategories(
  equipmentId: string,
  params: HierarchyListParams = {},
): Promise<HierarchyListResult<InterventionCategory>> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/equipment/${encodeURIComponent(equipmentId.trim())}/intervention-categories`,
      { params: listQueryParams(params) },
    ),
  );

  return parseListResult(data, normalizeCategory, page, pageSize);
}

export async function fetchInterventionCategory(
  id: string,
): Promise<InterventionCategory> {
  const trimmedId = id.trim();
  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/intervention-categories/${encodeURIComponent(trimmedId)}`,
    ),
  );

  const payload = unwrapEntityPayload(data);
  if (payload) {
    const category = normalizeCategory(payload);
    if (category) return category;
  }

  throw apiErrorFromEnvelope(
    { success: false, code: 404, errorMessage: "Category not found." },
    ERROR_CONTEXT,
  );
}

export async function createInterventionCategory(
  equipmentId: string,
  values: CategoryFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.post<BackendApiResponse<unknown>>(
      `/equipment/${encodeURIComponent(equipmentId.trim())}/intervention-categories`,
      toCategoryBody(values),
    ),
  );
}

export async function updateInterventionCategory(
  id: string,
  values: CategoryFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.patch<BackendApiResponse<unknown>>(
      `/intervention-categories/${encodeURIComponent(id.trim())}`,
      toCategoryBody(values),
    ),
  );
}

export async function deleteInterventionCategory(id: string): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.delete<BackendApiResponse<unknown>>(
      `/intervention-categories/${encodeURIComponent(id.trim())}`,
    ),
  );
}

export async function fetchInterventions(
  categoryId: string,
  params: HierarchyListParams = {},
): Promise<HierarchyListResult<Intervention>> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/intervention-categories/${encodeURIComponent(categoryId.trim())}/interventions`,
      { params: listQueryParams(params) },
    ),
  );

  return parseListResult(data, normalizeIntervention, page, pageSize);
}

export async function fetchIntervention(id: string): Promise<Intervention> {
  const trimmedId = id.trim();
  const data = await unwrapEnvelope(
    axiosPrivate.get<BackendApiResponse<unknown>>(
      `/interventions/${encodeURIComponent(trimmedId)}`,
    ),
  );

  const payload = unwrapEntityPayload(data);
  if (payload) {
    const intervention = normalizeIntervention(payload);
    if (intervention) return intervention;
  }

  throw apiErrorFromEnvelope(
    { success: false, code: 404, errorMessage: "Intervention not found." },
    ERROR_CONTEXT,
  );
}

export async function createIntervention(
  categoryId: string,
  values: InterventionFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.post<BackendApiResponse<unknown>>(
      `/intervention-categories/${encodeURIComponent(categoryId.trim())}/interventions`,
      toInterventionBody(values),
    ),
  );
}

export async function updateIntervention(
  id: string,
  values: InterventionFormValues,
): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.patch<BackendApiResponse<unknown>>(
      `/interventions/${encodeURIComponent(id.trim())}`,
      toInterventionBody(values),
    ),
  );
}

export async function deleteIntervention(id: string): Promise<HierarchyMutationResult> {
  return unwrapMutation(
    axiosPrivate.delete<BackendApiResponse<unknown>>(
      `/interventions/${encodeURIComponent(id.trim())}`,
    ),
  );
}

export function getHierarchyTotalPages(
  result: HierarchyListResult<unknown>,
): number {
  return getTotalPages(result);
}
